import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { isSelfShowingListing, hasListing3DTour } from 'common/listingHelpers';
import {
  withFeedCategory,
  actionFeedCategoryLoadlistings,
} from 'client/helpers/feedCategoryHelpers';
import {
  getCenterByArea,
  getMarkerIconConfig,
  getMarkerLabelConfig,
  GOOGLE_MAPS_MARKER_SHAPE_BOX,
  GOOGLE_MAPS_MARKER_SHAPE_CIRCLE,
  GOOGLE_MAPS_MARKER_THEME_LISTING,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED_VIEWING,
  GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWING,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED,
  GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWED,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED_VIEWED,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SELF_TOUR,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SELF_TOUR_VIEWING,
  GOOGLE_MAPS_MARKER_THEME_LISTING_SELF_TOUR_VIEWED,
  GOOGLE_MAPS_MARKER_THEME_LISTING_3D_TOUR,
  GOOGLE_MAPS_MARKER_THEME_LISTING_3D_TOUR_VIEWING,
  GOOGLE_MAPS_MARKER_THEME_LISTING_3D_TOUR_VIEWED,
} from 'client/helpers/googleMapsHelpers';

import MapViewManagerActions from 'client/actions/MapViewManager';
import UserActions from 'client/actions/User';

import SrListingsMapViewManager from 'client/components/SrListingsMapViewManager';
import SrSwipeableListingCard from 'client/components/SrSwipeableListingCard';

function createMarkerShape() {
  return ({ mapZoomLevel }) => {
    return mapZoomLevel >= 12 ? GOOGLE_MAPS_MARKER_SHAPE_BOX : GOOGLE_MAPS_MARKER_SHAPE_CIRCLE;
  };
}

function createMarkerConfig({ currentUser, viewingListing, bookmarksByListingId, viewedListings }) {
  const calculateMarkerShape = createMarkerShape();

  return ({ googleMaps, map, listing }) => {
    const markerShape = calculateMarkerShape({ mapZoomLevel: map.getZoom(), listing });
    const isSelfShowing = isSelfShowingListing(listing);
    const has3dTour = hasListing3DTour(listing);
    let markerTheme = GOOGLE_MAPS_MARKER_THEME_LISTING;

    if (viewingListing && viewingListing.id === listing.id) {
      markerTheme = has3dTour
        ? GOOGLE_MAPS_MARKER_THEME_LISTING_3D_TOUR_VIEWING
        : bookmarksByListingId.includes(`${listing.id}`)
        ? GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED_VIEWING
        : isSelfShowing
        ? GOOGLE_MAPS_MARKER_THEME_LISTING_SELF_TOUR_VIEWING
        : GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWING;
    } else if (viewedListings.includes(listing.id)) {
      markerTheme = has3dTour
        ? GOOGLE_MAPS_MARKER_THEME_LISTING_3D_TOUR_VIEWED
        : bookmarksByListingId.includes(`${listing.id}`)
        ? GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED_VIEWED
        : isSelfShowing
        ? GOOGLE_MAPS_MARKER_THEME_LISTING_SELF_TOUR_VIEWED
        : GOOGLE_MAPS_MARKER_THEME_LISTING_VIEWED;
    } else if (has3dTour) {
      markerTheme = GOOGLE_MAPS_MARKER_THEME_LISTING_3D_TOUR;
    } else if (isSelfShowing) {
      markerTheme = GOOGLE_MAPS_MARKER_THEME_LISTING_SELF_TOUR;
    } else if (bookmarksByListingId.includes(`${listing.id}`)) {
      markerTheme = GOOGLE_MAPS_MARKER_THEME_LISTING_SAVED;
    }

    return {
      icon: getMarkerIconConfig({
        googleMaps,
        markerShape,
        markerTheme,
      }),
      label: getMarkerLabelConfig({
        markerShape,
        listing,
        currentUser,
      }),
    };
  };
}

function createGoogleMapsConfig(mapState) {
  return (googleMaps) => {
    return {
      center:
        mapState.mapCenter ||
        getCenterByArea({
          googleMaps,
          area: mapState.mapArea || mapState.currentUser.searchFilters.area,
        }),
      zoom: mapState.mapZoomLevel,
      gestureHandling: 'greedy',
      clickableIcons: false,
      zoomControlOptions: {
        position: googleMaps.ControlPosition.RIGHT_TOP,
      },
      disableDefaultUI: true,
    };
  };
}

@withFeedCategory((props) => ({
  feedCategoryId: props.feedCategoryId,
  skipInitialize: true,
}))
class SrFeedCategoryMapView extends Component {
  static propTypes = {
    mapState: PropTypes.object.isRequired,
    listingPhotoResolution: PropTypes.number.isRequired,
    dispatch: PropTypes.func.isRequired,
    feedCategoryId: PropTypes.string.isRequired,
    mapViewId: PropTypes.string.isRequired,
  };

  _onMapAreaChange = ({ mapArea }) => {
    const { dispatch, mapViewId } = this.props;
    dispatch(MapViewManagerActions.setValues, {
      mapViewId: mapViewId,
      values: { mapArea },
    });
    dispatch(this._getListings);
  };

  _onMapZoomChange = (mapZoomLevel) => {
    const { dispatch, mapViewId } = this.props;
    dispatch(MapViewManagerActions.setValues, {
      mapViewId: mapViewId,
      values: { mapZoomLevel },
    });
  };

  _onMapCenterChange = (mapCenter) => {
    const { dispatch, mapViewId } = this.props;
    dispatch(MapViewManagerActions.setValues, {
      mapViewId: mapViewId,
      values: { mapCenter },
    });
  };

  _onClickListing = (viewingListing) => {
    const { dispatch, mapViewId } = this.props;
    dispatch(MapViewManagerActions.setValues, {
      mapViewId: mapViewId,
      values: { viewingListing },
    });

    dispatch(UserActions.updateViewedListings, {
      viewedListingId: viewingListing.id,
    });
  };

  _onClickMap = () => {
    this._onSwipeListingCard();
  };

  _onSwipeListingCard = () => {
    const { mapViewId, dispatch } = this.props;
    const viewingListing = null;
    dispatch(MapViewManagerActions.setValues, {
      mapViewId: mapViewId,
      values: { viewingListing },
    });
  };

  _getListings = ({ state }) => {
    const { dispatch, feedCategoryId, mapViewId } = this.props;
    const mapState = state.get(['mapViewById', mapViewId]);

    dispatch(actionFeedCategoryLoadlistings, {
      feedCategoryId,
      mapArea: mapState.mapArea,
      onSuccess({ data }) {
        MapViewManagerActions.setValues(
          { state },
          {
            mapViewId: mapViewId,
            values: {
              listings: data,
              centerMarkers:
                mapState.centerMarkers && mapState.listings.length === 0 && data.length >= 1,
            },
          },
        );
      },
    });
  };

  render() {
    const { mapState, listingPhotoResolution } = this.props;
    let srSwipeableListingCardDOM = null;

    if (mapState.viewingListing) {
      srSwipeableListingCardDOM = (
        <SrSwipeableListingCard
          listing={mapState.viewingListing}
          currentUser={mapState.currentUser}
          onSwipe={this._onSwipeListingCard}
          photoResolution={listingPhotoResolution}
        />
      );
    }

    return (
      <div>
        <SrListingsMapViewManager
          listings={mapState.listings}
          viewingListing={mapState.viewingListing}
          bookmarksByListingId={mapState.bookmarksByListingId}
          mapZoomLevel={mapState.mapZoomLevel}
          mapCenter={mapState.mapCenter}
          centerMarkers={mapState.centerMarkers}
          getMarkerConfig={createMarkerConfig(mapState)}
          getMarkerShape={createMarkerShape(mapState)}
          getGoogleMapsConfig={createGoogleMapsConfig(mapState)}
          onMapAreaChange={this._onMapAreaChange}
          onMapZoomChange={this._onMapZoomChange}
          onMapCenterChange={this._onMapCenterChange}
          onClickMap={this._onClickMap}
          onClickListing={this._onClickListing}
        />
        {srSwipeableListingCardDOM}
      </div>
    );
  }
}

export default SrFeedCategoryMapView;
